import React, { useState, useRef } from 'react';
import { Tree } from 'react-arborist';
import { 
  FolderIcon, FileIcon, ChevronRightIcon, ChevronDownIcon, 
  ImageIcon, FileTextIcon, MusicIcon, Code, 
  LayoutGrid, List, Upload, MoreVertical, Trash, Edit, Sparkles, MonitorPlay
} from 'lucide-react';
import { useProjectStore } from '../../stores/projectStore';
import { useIDEStore } from '../../stores/ideStore';

interface AssetNode {
  id: string;
  name: string;
  children?: AssetNode[];
  isFolder?: boolean;
  type?: 'image' | 'script' | 'audio' | 'model' | 'scene' | 'texture' | 'prefab' | 'material';
  path?: string;
}

type ViewMode = 'list' | 'grid';

const getFileIcon = (type?: string, size: number = 16) => {
  switch (type) {
    case 'image':
    case 'texture':
      return <ImageIcon size={size} className="text-purple-400" />;
    case 'script':
      return <Code size={size} className="text-blue-400" />;
    case 'audio':
      return <MusicIcon size={size} className="text-yellow-400" />;
    case 'scene':
      return <MonitorPlay size={size} className="text-orange-400" />;
    default:
      return <FileIcon size={size} className="text-gray-400" />;
  }
};

function buildAssetTree(files: any[]): AssetNode[] {
  const root: AssetNode = {
    id: 'root',
    name: 'Assets',
    isFolder: true,
    children: []
  };

  const folderMap = new Map<string, AssetNode>();
  folderMap.set('Assets', root);

  const sortedFiles = [...files].sort((a, b) => a.path.localeCompare(b.path));

  sortedFiles.forEach((file, index) => {
    const parts = file.path.split('/');
    let currentPath = 'Assets';
    let currentNode = root;

    for (let i = 0; i < parts.length - 1; i++) {
      const folderName = parts[i];
      currentPath += '/' + folderName;

      if (!folderMap.has(currentPath)) {
        const folderNode: AssetNode = {
          id: currentPath,
          name: folderName,
          isFolder: true,
          children: []
        };
        currentNode.children!.push(folderNode);
        folderMap.set(currentPath, folderNode);
      }

      currentNode = folderMap.get(currentPath)!;
    }

    const fileName = parts[parts.length - 1];
    currentNode.children!.push({
      id: `file-${index}`,
      name: fileName,
      type: file.type,
      path: file.path,
      isFolder: false
    });
  });

  return [root];
}

export function AssetsPanel({ className }: { className?: string }) {
  const { currentProject, openFile, updateFile } = useProjectStore();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number, y: number, asset: AssetNode | null } | null>(null);
  
  const assetTree = currentProject?.files ? buildAssetTree(currentProject.files) : [];
  
  // Flatten files for grid view (filtering out folders for now or showing all)
  const allFiles = currentProject?.files || [];

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    // Mock upload logic
    const files = Array.from(e.dataTransfer.files);
    console.log('Uploading files:', files.map(f => f.name));
    // Here we would call API to upload to R2
  };

  const handleContextMenu = (e: React.MouseEvent, asset: AssetNode) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, asset });
  };

  return (
    <div 
      className={`assets-panel ${className || ''}`} 
      onClick={() => setContextMenu(null)}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <div className="panel-header">
        <div className="flex items-center gap-2">
          <h3>Assets</h3>
          <span className="file-count">{currentProject?.files?.length || 0} items</span>
        </div>
        <div className="panel-toolbar">
          <button 
            className={`toolbar-btn ${viewMode === 'list' ? 'active' : ''}`} 
            onClick={() => setViewMode('list')}
            title="List View"
          >
            <List size={14} />
          </button>
          <button 
            className={`toolbar-btn ${viewMode === 'grid' ? 'active' : ''}`} 
            onClick={() => setViewMode('grid')}
            title="Grid View"
          >
            <LayoutGrid size={14} />
          </button>
          <button className="toolbar-btn" title="Upload Asset">
            <Upload size={14} />
          </button>
        </div>
      </div>

      <div className="panel-content assets-content relative">
        {dragOver && (
          <div className="absolute inset-0 bg-blue-500/20 border-2 border-blue-500 border-dashed z-50 flex items-center justify-center backdrop-blur-sm m-2 rounded-lg">
            <div className="text-center text-blue-200">
              <Upload size={48} className="mx-auto mb-2" />
              <p>Drop files to upload</p>
            </div>
          </div>
        )}

        {viewMode === 'list' ? (
          <Tree
            data={assetTree}
            openByDefault={false}
            width="100%"
            height={400}
            indent={24}
            rowHeight={28}
            onSelect={(nodes) => {
              if (nodes.length > 0) {
                const node = nodes[0];
                const path = node.data.path;
                setSelectedAsset(path || node.data.id);
                if (path && !node.data.isFolder) openFile(path);
              }
            }}
          >
            {({ node, style, dragHandle }) => (
              <div 
                style={style} 
                ref={dragHandle} 
                className={`tree-node ${selectedAsset === (node.data.path || node.data.id) ? 'selected' : ''}`}
                onContextMenu={(e) => handleContextMenu(e, node.data)}
              >
                <div className="tree-node-content">
                  {node.isLeaf ? (
                    getFileIcon(node.data.type)
                  ) : (
                    <>
                      {node.isOpen ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
                      <FolderIcon size={16} className="text-yellow-500" />
                    </>
                  )}
                  <span className="tree-node-name">{node.data.name}</span>
                </div>
              </div>
            )}
          </Tree>
        ) : (
          <div className="grid-view p-4 grid grid-cols-3 gap-4 auto-rows-min">
            {allFiles.map((file, idx) => (
              <div 
                key={idx}
                className={`asset-card ${selectedAsset === file.path ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedAsset(file.path);
                  openFile(file.path);
                }}
                onContextMenu={(e) => handleContextMenu(e, { id: file.path, name: file.path.split('/').pop()!, type: file.type, path: file.path })}
              >
                <div className="asset-preview">
                  {file.type === 'texture' || file.type === 'image' ? (
                    // Mock preview for now, in real app use file URL
                    <div className="bg-gray-800 w-full h-full flex items-center justify-center text-xs text-gray-500">
                      IMG
                    </div>
                  ) : (
                    getFileIcon(file.type, 32)
                  )}
                </div>
                <div className="asset-name truncate" title={file.path.split('/').pop()}>
                  {file.path.split('/').pop()}
                </div>
              </div>
            ))}
          </div>
        )}

        {!currentProject && (
          <div className="assets-empty">
            <p>No project loaded</p>
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div 
          className="context-menu fixed bg-[#2a2a2a] border border-[#444] rounded shadow-xl py-1 z-[100]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <div className="px-3 py-1 text-xs text-gray-500 border-b border-[#444] mb-1">
            {contextMenu.asset?.name}
          </div>
          <button className="context-item">
            <Edit size={12} /> Rename
          </button>
          <button className="context-item">
            <Trash size={12} /> Delete
          </button>
          <div className="h-px bg-[#444] my-1" />
          <button className="context-item text-purple-400">
            <Sparkles size={12} /> Ask Stan to Explain
          </button>
          {contextMenu.asset?.type === 'script' && (
            <button className="context-item text-blue-400">
              <Code size={12} /> Refactor Code
            </button>
          )}
        </div>
      )}

      <style jsx>{`
        .assets-panel {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .assets-content {
          flex: 1;
          overflow-y: auto;
        }
        .file-count {
          font-size: 10px;
          color: #666;
          margin-left: auto;
        }
        .toolbar-btn {
          padding: 4px;
          border-radius: 4px;
          color: #888;
        }
        .toolbar-btn:hover {
          color: white;
          background: #333;
        }
        .toolbar-btn.active {
          color: white;
          background: #444;
        }
        
        .tree-node {
          padding: 2px 8px;
          cursor: pointer;
        }
        .tree-node.selected {
          background: #375eab;
          color: white;
        }
        .tree-node-content {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .tree-node-name {
          font-size: 13px;
          font-family: 'Share Tech Mono', monospace;
        }

        .asset-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 8px;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .asset-card:hover {
          background: #333;
        }
        .asset-card.selected {
          background: #375eab;
          color: white;
        }
        .asset-preview {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #222;
          border-radius: 4px;
          overflow: hidden;
        }
        .asset-name {
          font-size: 11px;
          text-align: center;
          width: 100%;
          font-family: 'Share Tech Mono', monospace;
        }

        .context-item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 6px 12px;
          text-align: left;
          font-size: 12px;
          color: #ccc;
          background: none;
          border: none;
          cursor: pointer;
        }
        .context-item:hover {
          background: #375eab;
          color: white;
        }

        .assets-empty {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #666;
          font-size: 12px;
        }
      `}</style>
    </div>
  );
}
